/**
 * Một phiếu = một đơn vị tiền (model chặn phiếu trộn VND + USD: lệnh chi chỉ chở
 * được một loại tiền). Form chọn currency MỘT LẦN ở đầu bảng dòng; mỗi dòng chỉ có
 * một ô "Amount".
 *
 * DB/API vẫn lưu amount_vnd / amount_usd trên từng dòng (bút toán, HSBC, PDF đọc
 * hai cột đó) — file này là chỗ duy nhất đổi qua lại giữa hai cách biểu diễn.
 */
export const CURRENCIES = ["VND", "USD"];
export const DEFAULT_CURRENCY = "VND";

const num = (v) => Number(v) || 0;

// Cùng quy tắc với Erp::PaymentRequest::Item#currency: USD chỉ khi có USD và không có VND.
export const lineCurrencyOf = (item = {}) =>
  (!num(item.amount_vnd) && num(item.amount_usd) ? "USD" : "VND");

// Cùng quy tắc với Erp::PaymentRequest#payment_currency: có USD → phiếu USD.
export const requestCurrencyOf = (items = []) =>
  (items.some((i) => num(i.amount_usd)) ? "USD" : DEFAULT_CURRENCY);

/** Dòng API (amount_vnd/amount_usd) → dòng form (amount). */
export const toFormLine = (item = {}) => {
  const { amount_vnd: vnd, amount_usd: usd, ...rest } = item;
  return { ...rest, amount: num(vnd) || num(usd) };
};

/** Số tiền dòng form → hai cột của API theo currency của phiếu. */
export const toApiAmounts = (amount, currency) => (
  currency === "USD"
    ? { amount_vnd: 0, amount_usd: num(amount) }
    : { amount_vnd: num(amount), amount_usd: 0 }
);

export const hasAmounts = (items = []) => items.some((i) => num(i.amount) || num(i.vat));

/**
 * Dòng dựng từ hoá đơn (invoiceToLine / erpInvoiceToLine trả amount_vnd/amount_usd).
 * Hoá đơn khác currency của phiếu:
 *   - phiếu chưa có số tiền nào → đổi currency phiếu theo hoá đơn
 *   - đã có số tiền             → từ chối (không âm thầm biến USD thành VND)
 * → { line, currency } hoặc { error }.
 */
export const importLine = (rawLine, currentCurrency, items = []) => {
  const currency = lineCurrencyOf(rawLine);
  if (currency !== currentCurrency && hasAmounts(items)) {
    return {
      error: `This invoice is in ${currency} but the request is in ${currentCurrency}. ` +
        "A request carries one currency only — create a separate request for it.",
    };
  }
  return { line: toFormLine(rawLine), currency };
};

// Ký hiệu tiền cho bảng/tổng: "$1,000" và "1,000 ₫" (theo cách viết quen của mỗi loại).
export const CURRENCY_SYMBOLS = { USD: "$", VND: "₫" };

export const currencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || currency || "";

// "$" đứng trước số, "₫" (và mã khác) đứng sau — dùng cho ô nhập có ký hiệu bên cạnh.
export const symbolLeads = (currency) => currency === "USD";

export const formatCurrency = (value, currency) => {
  const amount = (Number(value) || 0).toLocaleString("en-US");
  const symbol = currencySymbol(currency);
  if (!symbol) return amount;
  return symbolLeads(currency) ? `${symbol}${amount}` : `${amount} ${symbol}`;
};
